import {
  Alignment,
  Button,
  Classes,
  Navbar,
  Panel,
  PanelStack2,
} from "@blueprintjs/core";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";
import * as Yup from "yup";

import {
  BeneficiarySearchFormDto,
  BeneficiarySearchGroupedResultDto,
  EditBeneficiaryPersonDto,
  EmailDto,
  Forme,
  SearchBeneficiaryDto,
  TelephoneDto,
} from "../../../api";
import { useCommonHooks } from "../../../hooks";
import { useCrudApi } from "../../../hooks/useCrudApi";
import { ETLCodes } from "../../../locales";
import { SiteTheme } from "../../../theme";
import { bceNumberValidate } from "../../../utils/bceNumberValidate";
import { CustomFormGenerator } from "../../CustomFormGenerator";
import BeneficiarySelectorCiteriasPanel, {
  IBeneficiarySelectorCiteriasPanelProps,
} from "./BeneficiarySelectorCiteriasPanel";
import { IBeneficiarySelectorResultsPanelProps } from "./BeneficiarySelectorResultsPanel";

const Container = styled.div`
  height: 100%;
  & .beneficiary-selector-panel-stack {
    height: 100%;
    min-height: 90vh;
    width: 100%;
    & .${Classes.PANEL_STACK2_VIEW} {
      padding: 1px;
      background-color: transparent !important;
    }
  }
`;

const NavBarStyled = styled(Navbar)<{ theme: SiteTheme }>`
  position: relative;
  height: 50px !important;
  color: ${(props) => props.theme.mainTextColor} !important;
  box-shadow: none !important;
  margin-bottom: 1rem;

  & .${Classes.NAVBAR_HEADING} {
    color: ${(props) => props.theme.mainTextColor} !important;
    font-weight: 600;
  }

  & > .${Classes.NAVBAR_GROUP} {
    height: 50px;
  }
`;

export interface IBeneficiarySelectorProps {
  onSelected: (selected: BeneficiarySearchGroupedResultDto) => void;
  onClose: () => void;
  telephone?: string;
}

export const BeneficiarySelector: React.FunctionComponent<
  IBeneficiarySelectorProps
> = ({ onSelected, onClose, telephone }) => {
  const { t, theme, apis } = useCommonHooks();

  const isEntreprise = React.useCallback(
    (forme: Forme) => [Forme.ChefEntreprise, Forme.Contact].includes(forme),
    []
  );

  const { data: typesTel } = useQuery(["types-tel-raw"], async () =>
    apis.referentials.phoneTypes()
  );
  const idtypeTelPerso = React.useMemo(
    () => typesTel?.find((tt) => tt.keyValue === "PERSO")?.idValue,
    [typesTel]
  );
  const idtypeTelGsmPerso = React.useMemo(
    () => typesTel?.find((tt) => tt.keyValue === "GSM_PERSO")?.idValue,
    [typesTel]
  );

  const { data: typesMail } = useQuery(["types-mail-raw"], async () =>
    apis.referentials.emailTypes()
  );
  const idtypeEmailPerso = React.useMemo(
    () => typesMail?.find((tt) => tt.keyValue === "PERSO")?.idValue,
    [typesMail]
  );

  const { data, validationErrors, onSave, isSaving } = useCrudApi({
    queryKey: ["beneficiary-suivi"],
    fetchFn: async () => {
      return SearchBeneficiaryDto.fromJS({ telephone });
    },
    saveFn: async (toSave: SearchBeneficiaryDto) => {
      let personPhones = [];
      if (toSave.telephone)
        personPhones.push(
          TelephoneDto.fromJS({
            numero: toSave.telephone,
            idtypeTelephone: +idtypeTelPerso,
          })
        );

      if (toSave.gsm)
        personPhones.push(
          TelephoneDto.fromJS({
            numero: toSave.gsm,
            idtypeTelephone: +idtypeTelGsmPerso,
          })
        );

      let personEmails = [];
      if (toSave.email)
        personEmails.push(
          EmailDto.fromJS({
            adresseEmail: toSave.email,
            idtypeEmail: +idtypeEmailPerso,
          } as EmailDto)
        );

      const saved = await apis.beneficiaryPersons.saveBeneficiaryPerson(
        EditBeneficiaryPersonDto.fromJS({
          ...toSave,
          telephones: personPhones,
          emails: personEmails,
          adresseDomicile: toSave.adresse,
          formeFromSuivi: toSave.forme,
        })
      );
      return SearchBeneficiaryDto.fromJS({ ...toSave, ...saved });
    },
    onSaved: (saved: SearchBeneficiaryDto) => {
      onSelected(
        BeneficiarySearchGroupedResultDto.fromJS({
          idpersonne: saved.idpersonne,
          nom: saved.nom,
          prenom: saved.prenom,
          formes: [
            BeneficiarySearchFormDto.fromJS({
              forme: saved.forme,
              idapprenant: saved.idapprenant,
              idformateur: saved.idformateur,
              idrepresentant: saved.idrepresentant,
              idchefEntreprise: saved.idchefEntreprise,
              idtuteur: saved.idtuteur,
              idcontact: saved.idcontact,
            }),
          ],
        })
      );
    },
    serverValidationRootKey: "EditSuiviDto",
  });

  const initialPanel: Panel<
    | IBeneficiarySelectorCiteriasPanelProps
    | IBeneficiarySelectorResultsPanelProps
  > = {
    renderPanel: BeneficiarySelectorCiteriasPanel,
    props: {
      isEntreprise,
      onSelect: onSelected,
      onClose,
      saving: isSaving,
      telephone,
    },
  };

  const [currentPanelStack, setCurrentPanelStack] = React.useState<
    Array<
      Panel<
        | IBeneficiarySelectorCiteriasPanelProps
        | IBeneficiarySelectorResultsPanelProps
      >
    >
  >([initialPanel]);

  const addToPanelStack = React.useCallback(
    (
      newPanel: Panel<
        | IBeneficiarySelectorCiteriasPanelProps
        | IBeneficiarySelectorResultsPanelProps
      >
    ) => setCurrentPanelStack((stack) => [...stack, newPanel]),
    []
  );
  const removeFromPanelStack = React.useCallback(
    () => setCurrentPanelStack(() => currentPanelStack.slice(0, -1)),
    [currentPanelStack]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape(
        {
          forme: Yup.string().when("isBeneficiaryCreation", {
            is: true,
            then: Yup.string().required(t(ETLCodes.Required)),
          }),
          numeroBce: Yup.string()
            .nullable()
            .test(
              "bce-validity",
              t(ETLCodes.InvalidBCE),
              (value) => !value || bceNumberValidate(value.toString())
            ),
          nom: Yup.string().when(["forme", "isBeneficiaryCreation"], {
            is: (forme, isBeneficiaryCreation) =>
              isBeneficiaryCreation && !isEntreprise(forme),
            then: Yup.string().required(t(ETLCodes.Required)),
          }),

          prenom: Yup.string().when(["forme", "isBeneficiaryCreation"], {
            is: (forme, isBeneficiaryCreation) =>
              isBeneficiaryCreation && !isEntreprise(forme),
            then: Yup.string().required(t(ETLCodes.Required)),
          }),
          gsm: Yup.string()
            .nullable()
            .when("isBeneficiaryCreation", {
              is: true,
              then: Yup.string()
                .nullable()
                .test(
                  "gsm-required",
                  t(ETLCodes.AtLeastOneContactField),
                  function (value, context: any) {
                    return (
                      !!value ||
                      !!context.from[0].value.telephone ||
                      !!context.from[0].value.email
                    );
                  }
                ),
            }),
          telephone: Yup.string()
            .nullable()
            .when("isBeneficiaryCreation", {
              is: true,
              then: Yup.string()
                .nullable()
                .test(
                  "tel-required",
                  t(ETLCodes.AtLeastOneContactField),
                  function (value, context: any) {
                    return (
                      !!value ||
                      !!context.from[0].value.gsm ||
                      !!context.from[0].value.email
                    );
                  }
                ),
            }),
          email: Yup.string()
            .nullable()
            .when("isBeneficiaryCreation", {
              is: true,
              then: Yup.string()
                .nullable()
                .test(
                  "email-required",
                  t(ETLCodes.AtLeastOneContactField),
                  function (value, context: any) {
                    return (
                      !!value ||
                      !!context.from[0].value.telephone ||
                      !!context.from[0].value.gsm
                    );
                  }
                ),
            }),
          idlieuFormation: Yup.number()
            .nullable()
            .when(
              [
                "forme",
                "isBeneficiaryCreation",
                "idsiegeSocial",
                "nomLieuFormation",
                "denominationSociale",
              ],
              {
                is: (
                  forme: Forme,
                  isBeneficiaryCreation: boolean,
                  idsiegeSocial: number,
                  nomLieuFormation: string,
                  denominationSociale: string
                ) =>
                  isBeneficiaryCreation &&
                  !nomLieuFormation &&
                  !!idsiegeSocial &&
                  forme === Forme.Tuteur,
                then: Yup.number()
                  .nullable()
                  .required(t(ETLCodes.Required)),
              }
            ),
          nomLieuFormation: Yup.string()
            .nullable()
            .when(
              [
                "forme",
                "isBeneficiaryCreation",
                "idsiegeSocial",
                "idlieuFormation",
                "denominationSociale",
              ],
              {
                is: (
                  forme: Forme,
                  isBeneficiaryCreation: boolean,
                  idsiegeSocial: number,
                  idlieuFormation: number,
                  denominationSociale: string
                ) =>
                  isBeneficiaryCreation &&
                  !idlieuFormation &&
                  !idsiegeSocial &&
                  !!denominationSociale &&
                  forme === Forme.Tuteur,
                then: Yup.string()
                  .nullable()
                  .required(t(ETLCodes.Required)),
              }
            ),
        },
        [
          ["gsm", "telephone"],
          ["gsm", "email"],
          ["email", "telephone"],
          ["idlieuFormation", "nomLieuFormation"],
        ]
      ),
    [isEntreprise, t]
  );

  return (
    <Container>
      <NavBarStyled theme={theme}>
        <Navbar.Group align={Alignment.LEFT}>
          <Navbar.Heading>{t(ETLCodes.SelectionBeneficiaire)}</Navbar.Heading>
        </Navbar.Group>
        <Navbar.Group align={Alignment.RIGHT}>
          <Button
            title={t(ETLCodes.Close)}
            icon="arrow-right"
            minimal={true}
            onClick={onClose}
          />
        </Navbar.Group>
      </NavBarStyled>
      <CustomFormGenerator
        initialValues={data}
        editable={true}
        editMode={true}
        saving={isSaving}
        validationSchema={validationSchema}
        validationErrors={validationErrors}
        onSubmit={onSave}
        preventDefaultSaveOnEnter
        deleteButtonPosition="none"
        saveButtonPosition="none"
        baseLeftPaddingPc={5}
        baseRightPaddingPc={5}
      >
        <PanelStack2
          className="beneficiary-selector-panel-stack"
          onOpen={addToPanelStack}
          onClose={removeFromPanelStack}
          renderActivePanelOnly={false}
          showPanelHeader={false}
          stack={currentPanelStack}
        />
      </CustomFormGenerator>
    </Container>
  );
};

export default BeneficiarySelector;
